import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/Button";
import { PageHeader } from "@/features/power_projects/components/ui";
import { useZECapabilities, usePatchZECapabilities, useEnergaBranches } from "../hooks";
import { usePatchWpwMailSettings, useWpwMailSettings } from "@/features/power_projects/hooks";

const ALL_PROJ_RKW = [
  "sieci nN napowietrzne",
  "sieci nN kablowe",
  "sieci SN kablowe",
  "sieci SN napowietrzne",
  "sieci nN w technologii PPN",
  "linie i aparatura WN",
  "stacje transformatorowe SN/nN",
  "linie światłowodowe kablowe",
  "linie światłowodowe na liniach napowietrznych",
  "kompleksowe roboty ogólnobudowlane",
];

const ALL_BUD_RKW = [
  "sieci nN napowietrzne",
  "sieci nN kablowe",
  "sieci SN kablowe",
  "sieci SN napowietrzne",
  "stacje transformatorowe SN/nN",
];

function CheckList({
  label,
  all,
  value,
  onChange,
}: {
  label: string;
  all: string[];
  value: string[];
  onChange: (v: string[]) => void;
}) {
  const toggle = (item: string) => {
    onChange(
      value.includes(item) ? value.filter((x) => x !== item) : [...value, item],
    );
  };
  return (
    <div>
      <p className="section-label mb-2">{label}</p>
      <div className="space-y-1.5">
        {all.map((item) => (
          <label key={item} className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={value.includes(item)}
              onChange={() => toggle(item)}
              className="accent-[var(--color-primary)]"
            />
            <span className="text-sm text-[var(--color-text-primary)]">
              {item}
            </span>
          </label>
        ))}
      </div>
    </div>
  );
}

function OddzialyEditor({
  value,
  onChange,
}: {
  value: string[];
  onChange: (v: string[]) => void;
}) {
  const { data: energa, isLoading } = useEnergaBranches();
  const branches = energa?.branches ?? [];
  const toggle = (name: string) => {
    onChange(value.includes(name) ? value.filter((x) => x !== name) : [...value, name]);
  };
  // Entries saved earlier that don't match any current ENERGA branch (legacy free-text).
  const branchNames = new Set(branches.map((b) => b.name));
  const legacy = value.filter((v) => !branchNames.has(v));

  return (
    <div>
      <p className="section-label mb-2">
        Oddziały ENERGA z porozumieniem (budowa)
      </p>
      {isLoading ? (
        <p className="text-xs text-[var(--color-text-muted)]">Ładowanie oddziałów…</p>
      ) : branches.length === 0 ? (
        <p className="text-xs text-amber-600">
          Brak oddziałów u klienta {energa?.client ?? "ENERGA"} — dodaj oddziały w kartotece klienta.
        </p>
      ) : (
        <div className="space-y-1.5">
          {branches.map((b) => (
            <label key={b.name} className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={value.includes(b.name)}
                onChange={() => toggle(b.name)}
                className="accent-[var(--color-primary)]"
              />
              <span className="text-sm text-[var(--color-text-primary)]">
                {b.display_name}
              </span>
            </label>
          ))}
        </div>
      )}
      {legacy.length > 0 && (
        <div className="mt-2">
          <p className="text-[10px] font-mono text-[var(--color-text-muted)] mb-1">
            Wpisane ręcznie (poza kartoteką):
          </p>
          <ul className="space-y-1">
            {legacy.map((o) => (
              <li key={o} className="flex items-center justify-between text-sm">
                <span className="text-[var(--color-text-secondary)]">{o}</span>
                <button
                  onClick={() => onChange(value.filter((x) => x !== o))}
                  className="text-xs text-[var(--color-text-tertiary)] hover:text-red-500"
                >
                  Usuń
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
      {value.length === 0 && (
        <p className="mt-2 text-xs text-amber-600">
          ⚠ Brak porozumień — roboty budowlane będą oznaczane jako niedostępne.
        </p>
      )}
    </div>
  );
}

/** Globalny próg automatycznej wysyłki listownej WPW (dni od przyjęcia zamówienia przez
 * podwykonawcę). Ustawia CEO; koordynator może nadpisać w swoim projekcie (profil ZE). */
export function WpwMailSettingsCard() {
  const { data, isLoading } = useWpwMailSettings();
  const patch = usePatchWpwMailSettings();
  const [days, setDays] = useState<string>("");
  const [touched, setTouched] = useState(false);
  useEffect(() => {
    if (data && !touched) setDays(String(data.auto_dispatch_days));
  }, [data?.updated_at, touched]);
  if (isLoading || !data) return null;
  const value = Number(days);
  const valid = Number.isInteger(value) && value >= 1 && value <= 365;
  const dirty = touched && value !== data.auto_dispatch_days;
  return (
    <div className="card p-4 space-y-3" data-testid="wpw-mail-settings">
      <p className="section-label">WPW — ścieżka listowna</p>
      <p className="text-xs text-[var(--color-text-secondary)]">
        Po ilu dniach od przyjęcia zamówienia WPW z sekwencji masowej przez podwykonawcę system sam zleca wysyłkę
        pakietu listownego, jeśli zgody nadal nie ma. Wartość globalna — koordynator może ją nadpisać w swoim
        projekcie (edycja projektu).
      </p>
      <div className="flex flex-wrap items-center gap-2">
        <input
          type="number"
          min={1}
          max={365}
          className="input-base w-24"
          value={days}
          onChange={(e) => { setDays(e.target.value); setTouched(true); }}
          aria-label="Dni do automatycznej wysyłki"
        />
        <span className="text-xs text-[var(--color-text-secondary)]">dni</span>
        <Button
          size="sm"
          disabled={!dirty || !valid || patch.isPending}
          loading={patch.isPending}
          onClick={() => patch.mutate({ auto_dispatch_days: value }, { onSuccess: () => setTouched(false) })}
        >
          Zapisz próg
        </Button>
        {!dirty && (
          <span className="text-xs text-[var(--color-text-tertiary)]">
            Obowiązuje: {data.auto_dispatch_days} dni
            {data.updated_by_name ? ` · ${data.updated_by_name}` : ""} · {new Date(data.updated_at).toLocaleDateString("pl-PL")}
          </span>
        )}
      </div>
      {patch.isError && (
        <p className="text-xs text-[var(--color-primary)] font-mono">Błąd zapisu. Próg zmienia tylko CEO.</p>
      )}
    </div>
  );
}

export function ZECapabilitiesPage() {
  const { data: caps, isLoading } = useZECapabilities();
  const patch = usePatchZECapabilities();

  const [projRkw, setProjRkw] = useState<string[]>([]);
  const [budRkw, setBudRkw] = useState<string[]>([]);
  const [oddzialy, setOddzialy] = useState<string[]>([]);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    if (!caps) return;
    setProjRkw(caps.proj_rkw ?? []);
    setBudRkw(caps.bud_rkw ?? []);
    setOddzialy(caps.porozumienia_oddzialy ?? []);
    setDirty(false);
  }, [caps?.updated_at]);

  const markDirty =
    <T,>(setter: (v: T) => void) =>
    (v: T) => {
      setter(v);
      setDirty(true);
    };

  const save = () => {
    patch.mutate(
      { proj_rkw: projRkw, bud_rkw: budRkw, porozumienia_oddzialy: oddzialy },
      { onSuccess: () => setDirty(false) },
    );
  };

  if (isLoading) return <div className="p-6 section-label">Ładowanie…</div>;

  return (
    <div className="p-4 md:p-6 max-w-2xl mx-auto space-y-6">
      <PageHeader
        title="Uprawnienia ENERGA"
        subtitle="GK ELEKTIM — zakres uprawnień projektowych i wykonawczych · tylko CEO"
        back="/ze-tenders"
      />

      {patch.isError && (
        <p className="text-xs text-[var(--color-primary)] font-mono">
          Błąd zapisu. Sprawdź czy masz uprawnienia CEO.
        </p>
      )}

      <div className="card p-4 space-y-5">
        <CheckList
          label="Uprawnienia PROJEKTOWE (zakres: projekt)"
          all={ALL_PROJ_RKW}
          value={projRkw}
          onChange={markDirty(setProjRkw)}
        />
        <div className="border-t border-[var(--color-border-muted)]" />
        <CheckList
          label="Uprawnienia WYKONAWCZE (zakres: wykonawstwo / budowa)"
          all={ALL_BUD_RKW}
          value={budRkw}
          onChange={markDirty(setBudRkw)}
        />
        <div className="border-t border-[var(--color-border-muted)]" />
        <OddzialyEditor value={oddzialy} onChange={markDirty(setOddzialy)} />
      </div>

      <WpwMailSettingsCard />

      <div className="flex items-center gap-3">
        <Button
          disabled={!dirty || patch.isPending}
          loading={patch.isPending}
          onClick={save}
        >
          Zapisz uprawnienia
        </Button>
        {!dirty && caps && (
          <span className="text-xs text-[var(--color-text-tertiary)]">
            Zaktualizowano:{" "}
            {new Date(caps.updated_at).toLocaleDateString("pl-PL")}
          </span>
        )}
      </div>
    </div>
  );
}
